import { Step2FormProps, WizzardStep2Form } from '../types'
import { useForm } from 'react-hook-form'
import * as yup from 'yup'
import { yupResolver } from '@hookform/resolvers/yup'
import React, { useCallback } from 'react'
import { DefaultValue } from '../constants'
import useActivationObject from '../../../providers/ActivationObjectProvider/hooks/useActivationObject'
import { ActivationObject } from '../../../providers/ActivationObjectProvider/types'
import { VALIDATION_REQUIRED } from '../../../../constants/validation'
import { isEmpty } from '../../../../utils/stringUtils'

const useStep2Form = (activationObject?: ActivationObject): Step2FormProps => {
    const propName = (name: keyof WizzardStep2Form): string => name

    const validationSchema = yup.object().shape({
        [propName('objectZIP')]: yup
            .string()
            .required(VALIDATION_REQUIRED)
            .matches(/^[0-9]/, 'Pole může obsahovat pouze čísla')
            .min(5, 'Pole musí obsahovat 5 znaků')
            .max(5, 'Pole musí obsahovat 5 znaků'),
        [propName('objectCity')]: yup.string().required(VALIDATION_REQUIRED),
        [propName('objectStreet')]: yup.string().required(VALIDATION_REQUIRED),
        [propName('objectCodeNumber')]: yup
            .string()
            .test('noslash', 'Pole nemůže obsahovat znak /', (value) => {
                return value === undefined || value.indexOf('/') === -1
            })
            .test('filledguid', 'Toto pole je povinné, je-li zadáno číslo orientační', (value, context) => {
                const guidNumber: string | undefined = context.parent['objectGuidanceNumber']
                return isEmpty(guidNumber) || (!isEmpty(value) && !isEmpty(guidNumber))
            })
            .test('filledatleast', 'Číslo popisné nebo orientační nebo parcelní nebo evidenční je povinné', (value, context) => {
                const guidNumber: string | undefined = context.parent['objectGuidanceNumber']
                const parcelNumber: string | undefined = context.parent['objectParcelNumber']
                const registrationNumber: string | undefined = context.parent['objectRegistrationNumber']
                return !isEmpty(value) || !isEmpty(guidNumber) || !isEmpty(parcelNumber) || !isEmpty(registrationNumber)
            }),
        [propName('objectGuidanceNumber')]: yup.string(),
        [propName('objectParcelNumber')]: yup.string(),
        [propName('objectRegistrationNumber')]: yup.string(),
        [propName('guaranteedArrivalTime')]: yup.string(),
        [propName('objectType')]: yup.string().required(VALIDATION_REQUIRED).min(1, VALIDATION_REQUIRED),
        [propName('objectTypeAnother')]: yup
            .string()
            .when('objectType', {
                is: 'Another',
                then: yup.string().required(VALIDATION_REQUIRED),
            })
            .max(20, 'Maximálně 20 znaků'),
        [propName('objectDesription')]: yup.string().required(VALIDATION_REQUIRED),
        [propName('objectSize')]: yup
            .string()
            .nullable()
            .when('personType', { is: 'Legal', then: yup.string().required(VALIDATION_REQUIRED).min(1, VALIDATION_REQUIRED) }),
        [propName('confirmObjectGPS')]: yup.boolean().required().isTrue(),
    })

    const form = useForm<WizzardStep2Form>({
        criteriaMode: 'all',
        reValidateMode: 'onChange',
        resolver: yupResolver(validationSchema),
        defaultValues: DefaultValue,
    })

    const reset = form.reset
    React.useEffect(() => {
        if (activationObject) {
            reset({
                objectAddressID: activationObject.objectAddressID ?? DefaultValue.objectAddressID,
                objectZIP: activationObject.objectZIP ?? DefaultValue.objectZIP,
                objectCity: activationObject.objectCity ?? DefaultValue.objectCity,
                objectStreet: activationObject.objectStreet ?? DefaultValue.objectStreet,
                objectCodeNumber: activationObject.objectCodeNumber ?? DefaultValue.objectCodeNumber,
                objectGuidanceNumber: activationObject.objectGuidanceNumber ?? DefaultValue.objectGuidanceNumber,
                objectParcelNumber: activationObject.objectParcelNumber ?? DefaultValue.objectParcelNumber,
                objectRegistrationNumber: activationObject.objectRegistrationNumber ?? DefaultValue.objectRegistrationNumber,
                objectGPS:
                    activationObject.objectGPSLatitude && activationObject.objectGPSLongitude
                        ? {
                              lat: Number(activationObject.objectGPSLatitude),
                              lng: Number(activationObject.objectGPSLongitude),
                          }
                        : DefaultValue.objectGPS,
                guaranteedArrivalTime: activationObject.guaranteedArrivalTime ?? DefaultValue.guaranteedArrivalTime,
                objectType: activationObject.objectType == null ? '' : activationObject.objectType,
                personType: activationObject?.personType ?? DefaultValue.personType,
                objectTypeAnother: activationObject.objectTypeAnother ?? DefaultValue.objectTypeAnother,
                objectDesription: activationObject.objectDesription ?? DefaultValue.objectDesription,
                objectSize: activationObject.objectSize == null ? '' : activationObject.objectSize,
                arrivalVehicle: activationObject.arrivalVehicle ?? DefaultValue.arrivalVehicle,
                confirmObjectGPS: DefaultValue.confirmObjectGPS,
            })
        }
    }, [activationObject, reset])

    const { update } = useActivationObject()
    const onSubmit = useCallback(
        (data: WizzardStep2Form) => {
            if (activationObject) {
                const newObject: ActivationObject = {
                    ...activationObject,
                    ...data,
                    objectSize: data.objectSize === '' ? null : data.objectSize,
                    objectGPSLatitude: data.objectGPS?.lat.toFixed(6),
                    objectGPSLongitude: data.objectGPS?.lng.toFixed(6),
                    wizardStep: 'ObjectControl',
                }
                update(newObject)
            }
        },
        [activationObject, update]
    )

    return {
        form,
        onSubmit,
    }
}

export default useStep2Form
